import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, AlertOctagon, ShieldCheck, Gauge } from "lucide-react";
import { SECTORS } from "@/lib/constants";

// Illustrative model — NOT financial advice
function computeRisk({ sectorKey, revenueInr, exportShare, energyIntensity }) {
    const sector = SECTORS.find((s) => s.key === sectorKey) || SECTORS[0];
    // rough tCO2e = revenue (INR cr) * sector factor * energy multiplier
    const revenueCr = revenueInr / 1e7; // revenue passed in INR
    const emissions = revenueCr * sector.factor * (0.6 + energyIntensity / 100);

    // CBAM cost only on export share, illustrative 85 EUR/tCO2e ~ 7,650 INR
    const cbamPerT = 7650;
    const cbamCost = emissions * (exportShare / 100) * cbamPerT;

    // Cost of capital uplift (bps) — scale with emissions intensity
    const intensity = emissions / Math.max(revenueCr, 1); // tCO2e / cr
    const bpsUplift = Math.min(320, 60 + intensity * 12);

    // Compliance rating
    let compliance = "A";
    if (intensity > 6) compliance = "C";
    else if (intensity > 3) compliance = "B";

    // Composite risk score 0-100 (higher = riskier)
    const score = Math.min(
        100,
        Math.round(intensity * 8 + exportShare * 0.35 + energyIntensity * 0.25 + sector.factor * 10)
    );

    return {
        emissions: Math.round(emissions),
        cbamCost: Math.round(cbamCost),
        bpsUplift: Math.round(bpsUplift),
        compliance,
        score,
        intensity: intensity.toFixed(2)
    };
}

const formatINR = (n) => {
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
    return `₹${n.toLocaleString("en-IN")}`;
};

export default function RiskEstimator() {
    const [sectorKey, setSectorKey] = useState("cement");
    const [revenueCr, setRevenueCr] = useState(50); // Cr INR
    const [exportShare, setExportShare] = useState(35);
    const [energyIntensity, setEnergyIntensity] = useState(60);

    const result = useMemo(
        () =>
            computeRisk({
                sectorKey,
                revenueInr: revenueCr * 1e7,
                exportShare,
                energyIntensity
            }),
        [sectorKey, revenueCr, exportShare, energyIntensity]
    );

    const riskColor =
        result.score < 40 ? "text-emerald-400" : result.score < 70 ? "text-gold" : "text-red-400";

    return (
        <section
            id="estimator"
            className="relative py-24 lg:py-36 border-t border-navy/10"
            data-testid="risk-estimator-widget"
        >
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">Risk Estimator · v0.1</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]">
                            See your <em className="text-gold not-italic font-light">carbon-linked</em> exposure in seconds.
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            An illustrative model that translates your sector, revenue, export share, and energy intensity into a first-pass climate risk profile. Not investment advice — the real thing is what we deliver on the platform.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-navy/10 border border-navy/10">
                    {/* Left: inputs */}
                    <div className="lg:col-span-5 bg-white p-8 lg:p-10 flex flex-col gap-8">
                        <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50">
                            <span>Inputs</span>
                            <span className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-gold dot-live rounded-full"></span> Terminal
                            </span>
                        </div>

                        {/* Sector */}
                        <div>
                            <label className="block font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50 mb-3">
                                Industry Sector
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {SECTORS.map((s) => (
                                    <button
                                        key={s.key}
                                        onClick={() => setSectorKey(s.key)}
                                        data-testid={`estimator-sector-${s.key}`}
                                        className={`text-left px-3 py-2.5 text-[12px] tracking-wide transition-colors duration-300 border ${
                                            sectorKey === s.key
                                                ? "border-gold bg-gold/10 text-gold"
                                                : "border-navy/10 text-navy/70 hover:border-navy/30"
                                        }`}
                                    >
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <SliderField
                            label="Annual Revenue"
                            testId="estimator-input-revenue"
                            value={revenueCr}
                            setValue={setRevenueCr}
                            min={5}
                            max={2500}
                            step={5}
                            unit="₹ Cr"
                        />
                        <SliderField
                            label="Export Share (of revenue)"
                            testId="estimator-input-export"
                            value={exportShare}
                            setValue={setExportShare}
                            min={0}
                            max={100}
                            step={1}
                            unit="%"
                        />
                        <SliderField
                            label="Energy / Fuel Intensity"
                            testId="estimator-input-energy"
                            value={energyIntensity}
                            setValue={setEnergyIntensity}
                            min={10}
                            max={100}
                            step={1}
                            unit=""
                        />
                    </div>

                    {/* Right: results */}
                    <div className="lg:col-span-7 bg-paper p-8 lg:p-12 relative overflow-hidden">
                        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none"></div>

                        <div className="relative flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50 mb-8">
                            <span>Output · Illustrative</span>
                            <span>Sector: {SECTORS.find((s) => s.key === sectorKey)?.label}</span>
                        </div>

                        {/* Big score */}
                        <div className="relative flex items-end gap-6 pb-8 border-b border-navy/10">
                            <motion.div
                                key={result.score}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="flex items-baseline gap-2"
                                data-testid="estimator-result-score"
                            >
                                <span className={`font-serif text-[110px] md:text-[160px] leading-none ${riskColor}`}>
                                    {result.score}
                                </span>
                                <span className="font-mono text-sm text-navy/50">/ 100</span>
                            </motion.div>
                            <div className="pb-4 flex-1">
                                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50 mb-2">Composite Climate Risk</div>
                                <div className="w-full h-1 bg-navy/10 relative overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${result.score}%` }}
                                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                                        className={`h-full ${
                                            result.score < 40
                                                ? "bg-emerald-400"
                                                : result.score < 70
                                                ? "bg-gold"
                                                : "bg-red-400"
                                        }`}
                                    />
                                </div>
                                <div className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase flex justify-between text-navy/40">
                                    <span>LOW</span><span>MODERATE</span><span>ELEVATED</span>
                                </div>
                            </div>
                        </div>

                        <div className="relative grid grid-cols-2 gap-px bg-navy/10 mt-8" role="status" aria-live="polite" aria-label={`Estimated composite risk ${result.score} out of 100. Emissions ${result.emissions} tonnes CO2 equivalent per year. CBAM exposure ${formatINR(result.cbamCost)}. Cost of capital uplift ${result.bpsUplift} basis points. Compliance rating ${result.compliance}.`}>
                            <MetricCell
                                icon={<Gauge size={16} strokeWidth={1.5} />}
                                label="Est. Emissions"
                                value={`${result.emissions.toLocaleString("en-IN")}`}
                                unit="tCO₂e / yr"
                                testId="estimator-metric-emissions"
                            />
                            <MetricCell
                                icon={<AlertOctagon size={16} strokeWidth={1.5} />}
                                label="CBAM Exposure"
                                value={formatINR(result.cbamCost)}
                                unit="Annual, illustrative"
                                testId="estimator-metric-cbam"
                            />
                            <MetricCell
                                icon={<TrendingUp size={16} strokeWidth={1.5} />}
                                label="Cost of Capital Uplift"
                                value={`+${result.bpsUplift}`}
                                unit="bps (indicative)"
                                testId="estimator-metric-bps"
                            />
                            <MetricCell
                                icon={<ShieldCheck size={16} strokeWidth={1.5} />}
                                label="Compliance Rating"
                                value={result.compliance}
                                unit={`Intensity ${result.intensity} tCO₂e / Cr`}
                                testId="estimator-metric-compliance"
                            />
                        </div>

                        {/* What would reduce your risk — ranked levers */}
                        <div className="relative mt-8 border border-navy/10 bg-white p-6 lg:p-7" data-testid="estimator-levers">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-8 h-px bg-gold"></div>
                                <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold">
                                    What Would Reduce Your Risk
                                </span>
                            </div>
                            <div className="flex flex-col gap-4">
                                {[
                                    { rank: 1, name: "Renewable electricity (PPA-backed)", drop: 8 },
                                    { rank: 2, name: "Process efficiency & thermal recovery", drop: 6 },
                                    { rank: 3, name: "Export mix optimisation", drop: 4 }
                                ].map((l) => (
                                    <div key={l.rank} className="flex items-center justify-between gap-4 border-b border-navy/10 pb-3 last:border-b-0 last:pb-0">
                                        <div className="flex items-center gap-4 min-w-0">
                                            <span className="font-mono text-[11px] tracking-[0.2em] text-navy/40 w-6">
                                                0{l.rank}
                                            </span>
                                            <span className="font-serif text-[15px] lg:text-base text-navy truncate">
                                                {l.name}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-navy/40">
                                                Risk
                                            </span>
                                            <span className="font-serif text-lg text-gold">↓ {l.drop}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="relative mt-8 flex items-center justify-between">
                            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-navy/40">
                                Model v0.1 · not investment advice
                            </p>
                            <a href="/contact" className="btn-ghost !py-3 !px-5 !text-[11px]" data-testid="estimator-cta">
                                Generate Full Assessment <ArrowRight size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function SliderField({ label, value, setValue, min, max, step, unit, testId }) {
    const displayValue = unit === "₹ Cr"
        ? `₹${value} Cr`
        : unit === "%"
        ? `${value}%`
        : unit
        ? `${value} ${unit}`
        : `${value}`;
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <label className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50">{label}</label>
                <span className="font-mono text-sm text-gold" data-testid={`${testId}-value`}>{displayValue}</span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => setValue(Number(e.target.value))}
                data-testid={testId}
                className="w-full h-1 bg-navy/10 appearance-none cursor-pointer accent-gold outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
            />
        </div>
    );
}

function MetricCell({ icon, label, value, unit, testId }) {
    return (
        <div className="bg-paper p-6" data-testid={testId}>
            <div className="flex items-center gap-3 text-navy/50 mb-4">
                <span className="text-gold">{icon}</span>
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase">{label}</span>
            </div>
            <div className="font-serif text-3xl md:text-4xl text-navy leading-none">{value}</div>
            <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-navy/40">{unit}</div>
        </div>
    );
}
